import React from "react";
import {Typography} from "@mui/material";
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import {
  DeviceMetric,
  getGaugeValue,
  getMetricRangeLabels,
  getMetricStatus,
  getMetricStatusText,
  MetricRange
} from "../utils/deviceInfo";

// 측정 항목 한 줄
// 정상 범위가 정해진 항목은 표준이하 / 표준 / 표준이상 3구간 바로, 그렇지 않은 항목은 최소~최대 위치 바로 표시
// normalRange 를 넘기면 항목에 고정된 정상 범위 대신 그 값을 기준으로 판정 (체성분의 키 / 성별 기준 표준 범위)
const MetricBar = (props: { metric: DeviceMetric, value: number, normalRange?: MetricRange }) => {
  const value = props.value;
  const metric = props.normalRange === undefined
    ? props.metric
    : { ...props.metric, normalMin: props.normalRange.min, normalMax: props.normalRange.max };

  const min = metric.min === undefined ? 0 : metric.min;
  const max = metric.max === undefined ? 100 : metric.max;
  const hasNormalRange = metric.normalMin !== undefined && metric.normalMax !== undefined;

  const status = getMetricStatus(metric, value);
  const isMeasured = value !== undefined && value !== null && value !== 0;

  return (
    <Box sx={{ py: .8 }}>
      <Stack direction={'row'} alignItems={'baseline'} justifyContent={'space-between'}>
        <Stack direction={'row'} alignItems={'center'}>
          <Typography sx={{ fontSize: 14, color: '#555', fontWeight: 500 }}>{metric.label}</Typography>
          {/* 정상 범위가 정해진 항목만 판정 결과를 표시 */}
          {hasNormalRange ? (
            <Typography sx={{ ...StatusChipStyle, color: status.color, borderColor: status.color }}>
              {getMetricStatusText(metric, status)}
            </Typography>
          ) : null}
        </Stack>
        <Typography component="div" sx={{ fontSize: 18, color: '#333', fontWeight: 700, mr: 1 }}>
          {value === undefined ? '-' : value}
          <Typography component="span" sx={{ fontSize: 14, color: '#999' }}> {metric.unit}</Typography>
        </Typography>
      </Stack>

      {hasNormalRange ? (
        <RangeBar metric={metric} value={value} min={min} max={max} status={status} isMeasured={isMeasured} />
      ) : (
        <PositionBar metric={metric} value={value} min={min} max={max} />
      )}
    </Box>
  );
}

// 표준이하 / 표준 / 표준이상 3구간 바
// 구간마다 실제 폭이 크게 달라 눈에 잘 들어오지 않으므로 화면에서는 세 구간을 같은 너비로 그림
const RangeBar = (props: {
  metric: DeviceMetric, value: number, min: number, max: number, status: { color: string }, isMeasured: boolean
}) => {
  const metric = props.metric;
  const normalMin = metric.normalMin as number;
  const normalMax = metric.normalMax as number;
  const labels = getMetricRangeLabels(metric);

  const activeIndex = !props.isMeasured ? -1 : (props.value < normalMin ? 0 : (props.value > normalMax ? 2 : 1));

  return (
    <Box>
      {/* 측정값 위치 */}
      <Box sx={{ position: 'relative', height: 7 }}>
        {activeIndex === -1 ? null : (
          <Box sx={{ position: 'absolute', bottom: 1, left: getMarkerPercent(props) + '%', transform: 'translateX(-50%)' }}>
            <Box sx={{
              width: 0, height: 0,
              borderLeft: '4px solid transparent',
              borderRight: '4px solid transparent',
              borderTop: '6px solid ' + props.status.color
            }} />
          </Box>
        )}
      </Box>

      <Stack direction={'row'} sx={{ height: 18, borderRadius: '4px', overflow: 'hidden', bgcolor: '#f2f2f2' }}>
        {labels.map((label, index) => (
          <Stack key={label} justifyContent={'center'} alignItems={'center'} sx={{
            flex: 1,
            bgcolor: index === activeIndex ? props.status.color : 'transparent',
            borderRight: index < labels.length - 1 ? '2px solid #fff' : 'none'
          }}>
            <Typography sx={{
              fontSize: 10,
              fontWeight: index === activeIndex ? 700 : 500,
              color: index === activeIndex ? '#fff' : '#aaa'
            }}>
              {label}
            </Typography>
          </Stack>
        ))}
      </Stack>

      {/* 구간 경계값 */}
      <Box sx={{ position: 'relative', height: 13, mt: .2 }}>
        <Typography sx={{ ...ScaleTextStyle, position: 'absolute', left: 0 }}>{props.min}</Typography>
        <Typography sx={{ ...ScaleTextStyle, position: 'absolute', left: '33.33%', transform: 'translateX(-50%)', color: '#279700' }}>{normalMin}</Typography>
        <Typography sx={{ ...ScaleTextStyle, position: 'absolute', left: '66.66%', transform: 'translateX(-50%)', color: '#279700' }}>{normalMax}</Typography>
        <Typography sx={{ ...ScaleTextStyle, position: 'absolute', right: 0 }}>{props.max}</Typography>
      </Box>
    </Box>
  );
}

// 3구간을 같은 너비로 그리므로 측정값도 자기 구간 안에서의 위치로 환산해야 함
function getMarkerPercent(props: { metric: DeviceMetric, value: number, min: number, max: number }) {
  const normalMin = props.metric.normalMin as number;
  const normalMax = props.metric.normalMax as number;
  const section = 100 / 3;

  if (props.value < normalMin) {
    return section * getRatio(props.value, props.min, normalMin);
  }
  if (props.value > normalMax) {
    return section * 2 + section * getRatio(props.value, normalMax, props.max);
  }
  return section + section * getRatio(props.value, normalMin, normalMax);
}

function getRatio(value: number, from: number, to: number) {
  if (to <= from) {
    return 0.5;
  }
  return Math.min(1, Math.max(0, (value - from) / (to - from)));
}

// 판정 기준이 없는 항목 - 최소~최대 범위에서의 위치만 표시
const PositionBar = (props: { metric: DeviceMetric, value: number, min: number, max: number }) => (
  <Box>
    <Box sx={{ height: 8, borderRadius: '4px', bgcolor: '#eee', mt: .8 }}>
      <Box sx={{
        height: '100%',
        width: getGaugeValue(props.metric, props.value) + '%',
        bgcolor: DEFAULT_BAR_COLOR,
        borderRadius: '4px'
      }} />
    </Box>
    <Stack direction={'row'} justifyContent={'space-between'} sx={{ mt: .2 }}>
      <Typography sx={ScaleTextStyle}>{props.min}</Typography>
      <Typography sx={ScaleTextStyle}>{props.max}</Typography>
    </Stack>
  </Box>
);

// 판정 기준이 없는 항목에 쓰는 바 색상
const DEFAULT_BAR_COLOR = '#33cccc';

const ScaleTextStyle = {
  fontSize: 10, color: '#bbb'
};

const StatusChipStyle = {
  ml: .8, fontSize: 11, fontWeight: 700,
  border: '1px solid', borderRadius: '8px', p: '0 6px', lineHeight: 1.6
};

export default MetricBar;
